import { formatBytes, truncate } from '~/helper/index'
import chalk from 'chalk'
import { currentHeapStatsSave } from '~/helper/node'
import { isTest } from '~/helper/nuxt'

type Emoji = string | '🔴' | '🟡' | '🟢' | '🔵' | '🐑' | '🐷' | '📄'

/**
 * Logs with fancy emoji, trace and yellow background
 */
export function logDetailed(message: string, options?: { emoji?: Emoji; body: string }) {
  if (!message) {
    return
  }
  // const header = truncate(message, 25)
  const header = chalk.bgHex('#fff393').black(`${options?.emoji || ''} ${message}`)
  console.groupCollapsed(header)
  if (options?.body) {
    console.log(chalk.grey(options.body))
  }
  console.groupEnd()
}

/**
 * Creates a logger instance compatible with the `using` keyword for automatic cleanup.
 *
 * Use `addMarker()` to include timestamps and heap allocation data in the logs.
 */
export function timeSpentLogger(label: string) {
  const GREEN_MB = 0
  const RED_MB = 150 * 1024 * 1024 // 150mb
  const start = Date.now()
  const startHeapStats = currentHeapStatsSave()
  let lastTimestamp = start
  let lastHeapTotal = startHeapStats.total
  const markers: {
    label: string
    durationMs: number
    heapTotal: number
    heapDelta: number
    totalHeapDelta: number
  }[] = []

  function formatSeconds(ms: number) {
    return `${(ms / 1000).toFixed(1)}s`
  }

  function elapsedTime() {
    return formatSeconds(Date.now() - start)
  }

  function formatHeap(heapTotal: number) {
    return formatBytes(heapTotal)
  }

  function formatDelta(bytes: number) {
    if (bytes === 0) {
      return formatBytes(bytes, 'kb')
    }
    const sign = bytes > 0 ? '+' : '-'
    return `${sign}${formatBytes(Math.abs(bytes))}`
  }

  function totalDeltaColor(bytes: number) {
    const clamped = Math.min(Math.max(bytes, GREEN_MB), RED_MB)
    const ratio = RED_MB === GREEN_MB ? 1 : (clamped - GREEN_MB) / (RED_MB - GREEN_MB)
    const startColor = { r: 34, g: 197, b: 94 }
    const endColor = { r: 239, g: 68, b: 68 }
    const r = Math.round(startColor.r + (endColor.r - startColor.r) * ratio)
    const g = Math.round(startColor.g + (endColor.g - startColor.g) * ratio)
    const b = Math.round(startColor.b + (endColor.b - startColor.b) * ratio)
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
  }

  console.log(`${chalk.cyan('⏱')} ${label}`)
  const interval = setInterval(async () => {
    const heapStats = currentHeapStatsSave()
    const currentMarker = markers[markers.length - 1]
    let message = label
    if (currentMarker) {
      message += ` (${currentMarker.label})`
    }
    console.log(
      `${chalk.dim('⌛')} ${truncate(message, 90)}  ${chalk.dim('elapsed')} ${elapsedTime()}  ${chalk.dim('heap')} ${formatHeap(heapStats.total)}  ${chalk.dim('step Δ')} ${formatDelta(heapStats.total - lastHeapTotal)}`,
    )
  }, 5000)

  return {
    addMarker: (text: string) => {
      const now = Date.now()
      const heapStats = currentHeapStatsSave()
      const durationMs = now - lastTimestamp
      markers.push({
        label: text,
        durationMs,
        heapTotal: heapStats.total,
        heapDelta: heapStats.total - lastHeapTotal,
        totalHeapDelta: heapStats.total - startHeapStats.total,
      })
      lastTimestamp = now
      lastHeapTotal = heapStats.total
    },
    [Symbol.dispose]: async () => {
      clearInterval(interval)
      const finalHeapStats = currentHeapStatsSave()
      const totalHeapDelta = finalHeapStats.total - startHeapStats.total
      const rows = markers.map((marker) => ({
        Step: marker.label,
        Time: formatSeconds(marker.durationMs),
        Heap: formatHeap(marker.heapTotal),
        'Step Δ': formatDelta(marker.heapDelta),
        'Total Δ': formatDelta(marker.totalHeapDelta),
      }))

      if (rows.length && isTest) {
        console.table(rows)
      }
      console.log(`${chalk.green('✓')} Finished: ${label}`)
      const totalPrefix = `${chalk.bold('Total')} ${chalk.dim('time')} ${formatSeconds(Date.now() - start)}`
      if (!isTest) {
        console.log(totalPrefix)
        return
      }
      const totalSuffix = `${chalk.dim('heap')} ${formatHeap(finalHeapStats.total)}  ${chalk.dim('step Δ')} ${formatDelta(finalHeapStats.total - lastHeapTotal)}  ${chalk.dim('total Δ')} ${chalk.hex(totalDeltaColor(totalHeapDelta))(formatDelta(totalHeapDelta))}`
      console.log(`${totalPrefix}  ${totalSuffix}`)
    },
  }
}
